/* eslint-disable */
import axios from "axios"
import { useState, useMemo, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { Spinner } from "reactstrap"
import DataTable from "react-data-table-component"

// ─────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────
const initials = n =>
  (n || "?")
    .split(" ")
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

const formatCallStatus = value => {
  if (!value) return "—"
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, letter => letter.toUpperCase())
}

// Today's date as YYYY-MM-DD, matching the value format <input type="date"> expects
const todayStr = () => {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const statusOf = t => {
  if (!t) return "None"
  if (t.completed_at || t.completed) return "Completed"
  if (t.scheduled_at || t.scheduled) return "Scheduled"
  if (t.sent_at || t.sent || t.ts_sent) return "Sent"
  return "None"
}

// Returns a display-safe date: pass-through ISO, null for sentinel strings
const safeDate = val => {
  if (!val) return null
  if (
    typeof val === "string" &&
    (val.startsWith("consent-") || val === "sent" || val === "accepted")
  )
    return null
  return val
}
const round2 = n => {
  if (n == null || isNaN(n)) return n
  return Math.round(Number(n) * 100) / 100
}

const scanEligible = c => c._screenResult === "Pass"

const SCREEN_BADGE = {
  Completed: "green",
  Scheduled: "amber",
  Sent: "blue",
  None: "gray",
}
const SCAN_BADGE = {
  Completed: "green",
  Scheduled: "amber",
  Sent: "blue",
  None: "gray",
  Locked: "lock",
}
const STAGE_BADGE = {
  Received: "gray",
  "Screening Round": "gray",
  Interview: "blue",
  "L1 Round": "blue",
  "Panel Round": "navy",
  Shortlisted: "navy",
  Offered: "green",
  Rejected: "red",
  Placed: "green",
  Potential: "navy",
}

// Parse "DD-MM-YYYY HH:MM AM/PM" strings returned by the screening_call / talent_scan API
const parseApiDate = str => {
  if (!str) return null
  // Already ISO format
  if (str.includes("T")) return str
  // Format: "17-06-2026 06:37 AM"
  const m = str.match(
    /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
  )
  if (!m) return str
  let [, dd, mo, yyyy, hh, mm, ampm] = m
  hh = parseInt(hh, 10)
  if (ampm.toUpperCase() === "PM" && hh !== 12) hh += 12
  if (ampm.toUpperCase() === "AM" && hh === 12) hh = 0
  return `${yyyy}-${mo}-${dd}T${String(hh).padStart(2, "0")}:${mm}:00`
}

// Sort newest-first by a date field, tolerant of the API's "DD-MM-YYYY hh:mm AM/PM" strings
const sortByDateDesc = (list, field) =>
  [...list].sort((a, b) => {
    const ta = new Date(parseApiDate(a?.[field]) || 0).getTime()
    const tb = new Date(parseApiDate(b?.[field]) || 0).getTime()
    return tb - ta
  })

const normalizeReport = raw => {
  const rows =
    raw?.results?.candidates ||
    raw?.candidates ||
    raw?.data?.candidates ||
    raw?.results ||
    raw?.data ||
    (Array.isArray(raw) ? raw : [])

  if (!Array.isArray(rows)) return []

  return rows.map(r => {
    const screenListRaw =
      r.screening_calls ||
      r.screening_call ||
      r.screening_test ||
      r.first_screening ||
      r.screen ||
      r.ai_screening ||
      []
    const screenHistory = sortByDateDesc(
      Array.isArray(screenListRaw) ? screenListRaw : [screenListRaw],
      "created_at"
    )
    const sc = screenHistory[0] || {}
    const scScheduled = sc.scheduled_at || null
    const screen = {
      sent_at: sc.sent_at || sc.created_at || null,
      scheduled_at: scScheduled,
      completed_at: sc.completed_at || null,
      score: sc.score ?? null,
      result: sc.result ?? null,
      _status: sc.status,
      _consent_id: sc.consent_id,
    }
    const screenResult =
      sc.result ||
      (sc.is_qualified != null ? (sc.is_qualified ? "Pass" : "Fail") : null)

    const scanListRaw =
  r.talent_scan || r.second_screening || r.scan || r.ts || []
const scanHistory = sortByDateDesc(
  Array.isArray(scanListRaw) ? scanListRaw : [scanListRaw],
  "sent_at"
)
const ts = scanHistory[0] || {}

const tsScoreObj = ts.score || null
const tsAts = tsScoreObj?.ats_details || {}

const scanScore =
  tsAts.total_score_secured != null
    ? tsAts.total_score_secured
    : tsScoreObj?.total_candidate_score ?? null

const scanResult =
  tsScoreObj == null
    ? null
    : tsAts.is_test_qualified === true
    ? "Pass"
    : tsAts.is_test_qualified === false
    ? "Fail"
    : null

const scan = {
  sent_at: ts.sent_at || null,
  scheduled_at: ts.scheduled_at || null,
  completed_at: ts.completed_at || null,
  score: round2(scanScore),
  totalScore: round2(tsAts.actual_total_score ?? tsScoreObj?.total_test_score ?? null), 
  percentage: tsAts.percentage_score ?? null,
  result: scanResult,
  booking_link: ts.booking_link || null,
  testName: tsScoreObj?.test_info?.name || null,
  isCheated: tsScoreObj?.test_info?.is_cheated ?? null,
  cheatedSection: tsScoreObj?.test_info?.cheated_section ?? null,
  sectionScores: tsScoreObj?.score_info_dict || null,
}
    return {
      name:
        [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" ") ||
        r.candidate_name ||
        r.name ||
        "—",
      email: r.email || r.candidate_email || "—",
      phone: r.mobile_phone || r.candidate_phone || "—",
      jobTitle: r.job_title || "",
      stage:
        r.pipeline_stage ||
        r.stage_name ||
        r.pipeline_status ||
        r.candidate_status ||
        r.application_status ||
        "—",
      recruiter: r.applied_by || r.added_by_name || "—",
      _screen: screen,
      _scan: scan,
      _screenHistory: screenHistory,
      _scanHistory: scanHistory,
      _screenResult: screenResult,
      _screenTime:
        screen.completed_at || screen.scheduled_at || screen.sent_at || "",
      _scanTime: scan.completed_at || scan.scheduled_at || scan.sent_at || "",
    }
  })
}

// ─────────────────────────────────────────────
//  Cell renderers (reused as DataTable column `cell` fns)
// ─────────────────────────────────────────────
function TimelineStep({ reached, cls, label, time }) {
  return (
    <div className={`step ${reached ? cls : "pending"}`}>
      <span className="dot" />
      <span className="lab">{label}</span>
      <span className={`time${reached ? "" : " muted"}`}>
        {reached ? time || "—" : "—"}
      </span>
    </div>
  )
}

function TimelineCell({
  test,
  badgeMap,
  history,
  onViewHistory,
  showResult = true,
}) {
  if (!test) test = {}
  const sent = safeDate(test.sent_at || test.sent || null)
  const sched = safeDate(test.scheduled_at || test.scheduled || null)
  const comp = safeDate(test.completed_at || test.completed || null)
  const status = statusOf(test)
  const score = test.score != null ? round2(test.score) : null
  const totalScore = test.totalScore ?? null          // ← ADD
  const pct = test.percentage != null ? test.percentage : null
  const result = test.result != null ? test.result : null
  const attempts = history?.length || 0

  let resRow = null
  if (comp && score != null) {
    const pf = result === "Pass" ? "pass" : "fail"
    const denominator = totalScore ?? 10                          // ← USE
    const barWidth = Math.min(100, (score / denominator) * 100)
    resRow = (
      <div className="res-row">
        <span className="score">
          {score}
         <span className="den">/{denominator}</span> 
        </span>
        <span className="scorebar">
          <i className={pf} style={{ width: `${score * 10}%` }} />
        </span>
        {showResult && <span className={`res-chip ${pf}`}>{result || "—"}</span>}
      </div>
    )
  }

  return (
    <div className="cellwrap">
      <div
        style={{
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span className={`badge ${badgeMap[status] || "gray"}`}>
          {status === "None" ? "Not started" : status}
        </span>
        {attempts > 1 && <span className="attempt-count">×{attempts}</span>}
      </div>
      <div className="tl">
        <TimelineStep reached={!!sent} cls="sent" label="Sent" time={sent} />
        <TimelineStep
          reached={!!sched}
          cls="sched"
          label="Scheduled"
          time={sched}
        />
        <TimelineStep
          reached={!!comp}
          cls="done"
          label="Completed"
          time={comp}
        />
      </div>
      {resRow}
      {attempts > 0 && (
        <button type="button" className="hist-link" onClick={onViewHistory}>
          {attempts > 1 ? `View latest · ${attempts} attempts` : "View details"}
        </button>
      )}
    </div>
  )
}

function ScanCell({ candidate, onViewHistory }) {
  if (
    !scanEligible(candidate) &&
    !candidate._scan?.sent_at &&
    !candidate._scan?.sent
  ) {
    const failed = candidate._screenResult === "Fail"
    return (
      <div className="cellwrap">
        <div style={{ marginBottom: 6 }}>
          <span className="badge lock">
            {failed ? "Not eligible" : "Locked"}
          </span>
        </div>
        <div className="locked">
          {failed ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <rect x="4" y="11" width="16" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
          )}
          <div className="lk-title">
            {failed
              ? "AI screening not passed"
              : "Unlocks after screening pass"}
          </div>
        </div>
      </div>
    )
  }
  return (
    <TimelineCell
      test={candidate._scan}
      badgeMap={SCAN_BADGE}
      history={candidate._scanHistory}
      onViewHistory={onViewHistory}
    />
  )
}

function HistoryPanel({ view, onClose }) {
  if (!view) return null
  const { name, type, rows, timezoneLabel } = view
  const isScan = type === "scan"

  return createPortal(
    <div className="hist-overlay" onClick={onClose}>
      <div className="hist-modal" onClick={e => e.stopPropagation()}>
        <div className="hist-head">
          <div>
            <div className="hist-title">
              {isScan ? "Talent Scan Test History" : "AI Screening Call History"}
            </div>
            <div className="hist-sub">
              {name} · {rows.length} attempt{rows.length === 1 ? "" : "s"}
            </div>
            <div className="hist-sub">Timezone: {timezoneLabel || "Timezone unavailable"}</div>
          </div>
          <button type="button" className="hist-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="hist-body">
          {rows.length === 0 ? (
            <div className="empty">No attempts recorded.</div>
          ) : (
            <table className="hist-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Sent By</th>
                  <th>{isScan ? "Sent" : "Requested"}</th>
                  <th>Scheduled</th>
                  <th>Completed</th>
                  {!isScan && <th>Call Status</th>}
                  <th>Score</th>
                  {isScan && <th>Qualified</th>}
                  {isScan && <th>Cheated</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  // ── Talent scan: score is a nested object, screen calls: score is a raw value ──
                  const scoreObj = isScan ? row.score : null
                  const ats = scoreObj?.ats_details || {}
                  const testInfo = scoreObj?.test_info || {}
                  const displayScore = isScan
                    ? scoreObj == null
                      ? null
                      : round2(ats.total_score_secured ?? scoreObj.total_candidate_score ?? null)
                    : row.score != null
                    ? row.score
                    : null

                  const totalPossible = isScan
                    ? round2(ats.actual_total_score ?? scoreObj?.total_test_score ?? null)
                    : null

                  const pct = isScan && ats.percentage_score != null
                    ? `${(ats.percentage_score * 100).toFixed(0)}%`
                    : null

                  const isQualified = isScan
                    ? scoreObj == null
                      ? null
                      : ats.is_test_qualified === true
                      ? "Pass"
                      : ats.is_test_qualified === false
                      ? "Fail"
                      : "—"
                    : null

                  const isCheated = isScan ? testInfo.is_cheated : null
                  const cheatedSection = isScan ? testInfo.cheated_section : null

                  return (
                    <tr
                      key={row.consent_id ?? row.booking_link ?? i}
                      className={i === 0 ? "latest" : ""}
                    >
                      <td>
                        {i === 0 ? <span>Latest</span> : <span className="muted">{rows.length - i}</span>}
                      </td>
                      <td className="dt">{row.created_by || "—"}</td>
                      <td className="dt">{row.requested || row.sent_at || row.created_at || "—"}</td>
                      <td className="dt">{row.scheduled_at || "—"}</td>
                      <td className="dt"style={!isScan ? { textTransform: "capitalize" } : undefined}>
                        {isScan
                          ? row.completed_at || "—"
                          : row.completed_at || formatCallStatus(row.status)}
                      </td>
                      {!isScan && (
                        <td style={{ textTransform: "capitalize" }}>
                          {formatCallStatus(row.call_status || row.status)}
                        </td>
                      )}
                      <td>
                        {displayScore != null
                          ? totalPossible != null
                            ? `${displayScore} / ${totalPossible}`
                            : displayScore
                          : "—"}
                      </td>
                      {isScan && (
                        <td>
                          <span className={`res-chip ${isQualified === "Pass" ? "pass" : isQualified === "Fail" ? "fail" : ""}`}>
                            {isQualified ?? "—"}
                          </span>
                        </td>
                      )}
                      {isScan && (
                        <td>
                          {isCheated === true
                            ? cheatedSection
                              ? `Yes (${cheatedSection})`
                              : "Yes"
                            : isCheated === false
                            ? "No"
                            : "—"}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
const dtStyles = {
  table: {
    style: {
      background: "transparent",
      border: "1px solid var(--line, #d8dce5)",
      borderRadius: "10px",
      overflow: "hidden",
    },
  },
  tableWrapper: {
    style: {
      border: "1px solid var(--line, #d8dce5)",
      borderRadius: "10px",
      boxShadow: "0 1px 3px rgba(16, 24, 40, 0.04)",
    },
  },
  headRow: {
    style: {
      background: "#f4f6fa",
      borderBottom: "2px solid var(--line, #d8dce5)",
      minHeight: "44px",
    },
  },
  headCells: {
    style: {
      fontSize: "12.5px",
      fontWeight: 700,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      color: "var(--navy, #000053)",
      paddingLeft: "14px",
      paddingRight: "14px",
      borderRight: "1px solid var(--line, #e4e7ec)",
    },
  },
  rows: {
    style: {
      borderBottom: "1px solid var(--line, #e4e7ec)",
      minHeight: "0px",
      "&:not(:last-of-type)": {
        borderBottomStyle: "solid",
      },
    },
    highlightOnHoverStyle: {
      backgroundColor: "#fbfbfd",
      borderBottomColor: "var(--line, #e4e7ec)",
      outline: "none",
    },
  },
  cells: {
    style: {
      paddingTop: "10px",
      paddingBottom: "10px",
      paddingLeft: "14px",
      paddingRight: "14px",
      alignItems: "flex-start",
      borderRight: "1px solid #eef0f4",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid var(--line, #e4e7ec)",
      minHeight: "48px",
    },
  },
}

export default function JobOrderReportDetail() {
  document.title = "ATS - Screening Report"

  const [jobList, setJobList] = useState([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobsError, setJobsError] = useState("")
  const [selectedJobId, setSelectedJobId] = useState("")
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false)
  const [jobSearch, setJobSearch] = useState("")
  const [jobPage, setJobPage] = useState(1)
  const [jobTotal, setJobTotal] = useState(0)
  const JOB_PAGE_SIZE = 10

  const [allCandidates, setAllCandidates] = useState([])
  const [reportLoading, setReportLoading] = useState(false)
  const [reportError, setReportError] = useState("")

  const [totalCount, setTotalCount] = useState(0)
  const [nextUrl, setNextUrl] = useState(null)
  const [prevUrl, setPrevUrl] = useState(null)
  const [serverPage, setServerPage] = useState(1)
  const PAGE_SIZE = 10

  const [summary, setSummary] = useState(null)
  const [reportTimezone, setReportTimezone] = useState("")
  const [screeningPct, setScreeningPct] = useState(null)   // ⬅️ NEW

  const [screenFilter, setScreenFilter] = useState("all")
  const [scanFilter, setScanFilter] = useState("all")
  const [fromDate, setFromDate] = useState(todayStr())
  const [toDate, setToDate] = useState(todayStr())
  const [search, setSearch] = useState("")

  const [historyView, setHistoryView] = useState(null)
  const openHistory = (candidate, type) => {
    const rows =
      type === "scan" ? candidate._scanHistory : candidate._screenHistory
    setHistoryView({ name: candidate.name, type, rows: rows || [], timezoneLabel })
  }

  const userInfos = JSON.parse(sessionStorage.getItem("user-info") || "{}")
  const companyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id ?? null
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const fetchJobs = useCallback(async (page = 1, searchText = "") => {
      setJobsLoading(true)
      setJobsError("")
      try {
        const res = await axios.get("/api/v1/create-job-order/", {
          params: {
            page,
            size: JOB_PAGE_SIZE,
            ...(searchText.trim() ? { search: searchText.trim() } : {}),
            ...(companyId ? { company_id: companyId } : {}),
          },
        })
        const jobs =
          res.data?.results ||
          res.data?.data?.results ||
          (Array.isArray(res.data) ? res.data : [])
        setJobList(jobs)
        setJobPage(page)
        setJobTotal(Number(res.data?.count ?? res.data?.data?.count ?? jobs.length))
        if (jobs.length)
          setSelectedJobId(String(jobs[0].id || jobs[0].job_id || ""))
      } catch (e) {
        setJobsError(e?.response?.data?.message || "Failed to load job orders.")
      } finally {
        setJobsLoading(false)
      }
  }, [companyId])

  useEffect(() => {
    fetchJobs(1, "")
  }, [fetchJobs])

  const fetchReportPage = useCallback(
    async (jobId, pgNum, overrides = {}) => {
      if (!jobId) return
      setReportLoading(true)
      setReportError("")
      try {
        const sf =
          overrides.screenFilter !== undefined
            ? overrides.screenFilter
            : screenFilter
        const tf =
          overrides.scanFilter !== undefined ? overrides.scanFilter : scanFilter
        const fd =
          overrides.fromDate !== undefined ? overrides.fromDate : fromDate
        const td = overrides.toDate !== undefined ? overrides.toDate : toDate
        const pp =
          overrides.rowsPerPage !== undefined
            ? overrides.rowsPerPage
            : rowsPerPage
          const sq =
            overrides.search !== undefined ? overrides.search : search   // ⬅️ added

        const params = { job_id: jobId, page: pgNum, size: pp }
        if (sf && sf !== "all") params.screening_filter = sf.toLowerCase()
        if (tf && tf !== "all") params.ts_filter = tf.toLowerCase()
        if (fd) params.from_date = fd
        if (td) params.to_date = td
        if (sq) params.search = sq   

        const res = await axios.get("/api/v1/screening-report/", { params })
        setAllCandidates(normalizeReport(res.data))
        setReportTimezone(res.data?.timezone || "")
        setTotalCount(res.data?.count ?? 0)
        setNextUrl(res.data?.next ?? null)
        setPrevUrl(res.data?.previous ?? null)
        setServerPage(pgNum)

        const rawSummary =
          res.data?.summary ||
          res.data?.stats ||
          res.data?.data?.summary ||
          res.data?.results?.summary ||
          null
        setSummary(rawSummary)
         const pct =
          res.data?.results?.screening_percentage ??
          res.data?.screening_percentage ??
          res.data?.data?.screening_percentage ??
          null
        setScreeningPct(pct)
      } catch (e) {
        setReportError(
          e?.response?.data?.message || "Failed to load screening report."
        )
        setSummary(null)
      } finally {
        setReportLoading(false)
      }
    },
    [screenFilter, scanFilter, fromDate, toDate, rowsPerPage,search]
  )

const handleSearchChange = e => {
  const val = e.target.value
  setSearch(val)

  if (val === "") {
    fetchReportPage(selectedJobId, 1, { search: "" })
  }
}
useEffect(() => {
  setSearch("")
}, [])
const handleSearchKeyDown = e => {
  if (e.key === "Enter") {
    fetchReportPage(selectedJobId, 1, { search: e.target.value })
  }
}
  const handlePerRowsChange = (newPerPage, pg) => {
    setRowsPerPage(newPerPage)
    fetchReportPage(selectedJobId, pg, { rowsPerPage: newPerPage })
  }

  const fetchReportPageRef = useRef(fetchReportPage)
  useEffect(() => {
    fetchReportPageRef.current = fetchReportPage
  }, [fetchReportPage])

  // ── Reset + fetch page 1 when job changes ──
  // Intentionally depends ONLY on selectedJobId — this should run when the user
  // picks a different job order, not whenever filter state happens to change.
  // Filters are explicitly reset to defaults (status: All, dates: today) every
  // time this fires, including on initial mount — not just relying on the
  // useState initializers, so the reset is guaranteed rather than incidental.
  useEffect(() => {
    if (!selectedJobId) return
    setAllCandidates([])
    setTotalCount(0)
    setNextUrl(null)
    setPrevUrl(null)
    setSummary(null)
    setServerPage(1)
    setScreenFilter("all")
    setScanFilter("all")
    setFromDate(todayStr())
    setToDate(todayStr())
    setSearch("")
    fetchReportPageRef.current(selectedJobId, 1, {
      screenFilter: "all",
      scanFilter: "all",
      fromDate: todayStr(),
      toDate: todayStr(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId])

  // ── Stats ───────────────────────────────────
  // Prefer job-wide totals bound from the API's summary block. Try a few likely
  // key-name variants so this binds correctly whatever the backend calls them.
  // Falls back to counting just the current page if no summary block is present.
  const pageStats = useMemo(
    () => ({
      scSent: allCandidates.filter(x => x._screen?.sent_at || x._screen?.sent)
        .length,
      scSched: allCandidates.filter(
        x => x._screen?.scheduled_at || x._screen?.scheduled
      ).length,
      scDone: allCandidates.filter(
        x => x._screen?.completed_at || x._screen?.completed
      ).length,
      tsElig: allCandidates.filter(x => scanEligible(x)).length,
      tsSent: allCandidates.filter(
        x => x._scan?.sent_at || x._scan?.sent || x._scan?.ts_sent
      ).length,
      tsDone: allCandidates.filter(
        x => x._scan?.completed_at || x._scan?.completed
      ).length,
    }),
    [allCandidates]
  )

  const stats = useMemo(() => {
    if (!summary) return pageStats
    const pick = (...keys) => {
      for (const k of keys) if (summary[k] != null) return summary[k]
      return null
    }
    return {
      scSent:
        pick("screening_call_sent", "sc_sent", "screen_sent") ?? pageStats.scSent,
      scSched:
        pick("screening_call_scheduled", "sc_scheduled", "screen_scheduled") ??
        pageStats.scSched,
      scDone:
        pick("screening_call_completed", "sc_completed", "screen_completed") ??
        pageStats.scDone,
      tsElig:
        pick(
          "talent_scan_eligible",
          "ts_eligible",
          "ts_sent",
          "talent_scan_sent"
        ) ?? pageStats.tsElig,
      tsSent: pick("talent_scan_scheduled", "ts_scheduled") ?? pageStats.tsSent,
      tsDone: pick("talent_scan_completed", "ts_completed") ?? pageStats.tsDone,
    }
  }, [summary, pageStats])
  const visibleRows = allCandidates

  const handleReset = () => {
    setScreenFilter("all")
    setScanFilter("all")
    setFromDate(todayStr())
    setToDate(todayStr())
    setSearch("")
    fetchReportPage(selectedJobId, 1, {
      screenFilter: "all",
      scanFilter: "all",
      fromDate: todayStr(),
      toDate: todayStr(),
    })
  }

  const selectedJob = jobList.find(
    j => String(j.id || j.job_id) === selectedJobId
  )
  const timezoneLabel = reportTimezone === "Asia/Kolkata"
    ? "IST"
    : reportTimezone === "America/New_York"
    ? "ET"
    : reportTimezone || "Timezone unavailable"

  const STATUS_COLORS = {
    Completed: { fill: "FFD1FAE5", font: "FF065F46" },
    Scheduled: { fill: "FFFEF3C7", font: "FF92400E" },
    Sent:      { fill: "FFDBEAFE", font: "FF1E40AF" },
    "None":    { fill: "FFF3F4F6", font: "FF6B7280" },
  }
  const STAGE_COLORS = {
    Received: "FFF3F4F6",
    "Screening Round": "FFF3F4F6",
    Interview: "FFDBEAFE",
    "L1 Round": "FFDBEAFE",
    "Panel Round": "FFE0E7FF",
    Shortlisted: "FFE0E7FF",
    Offered: "FFD1FAE5",
    Placed: "FFD1FAE5",
    Potential: "FFE0E7FF",
    Rejected: "FFFEE2E2",
  }

  const handleExport = async () => {
    try {
      const ExcelJSMod = await import("exceljs")
      const ExcelJS = ExcelJSMod.default || ExcelJSMod

      const wb = new ExcelJS.Workbook()
      wb.creator = "ATS"
      wb.created = new Date()
      const ws = wb.addWorksheet("Screening Report", {
        views: [{ state: "frozen", ySplit: 1 }],
      })

      const columns = [
        { header: "Candidate Name", key: "name", width: 24 },
        { header: "Email", key: "email", width: 26 },
        { header: "Phone", key: "phone", width: 16 },
        { header: "Stage", key: "stage", width: 16 },
        { header: "AI Screening Status", key: "scStatus", width: 16 },
        { header: "AI Screen Sent", key: "scSent", width: 16 },
        { header: "AI Screen Scheduled", key: "scSched", width: 16 },
        { header: "AI Screen Completed", key: "scDone", width: 16 },
        { header: "AI Screen Score", key: "scScore", width: 12 },
        { header: "AI Screen Attempts", key: "scAttempts", width: 12 },
        { header: "Talent Scan Status", key: "tsStatus", width: 16 },
        { header: "Talent Scan Sent", key: "tsSent", width: 16 },
        { header: "Talent Scan Scheduled", key: "tsSched", width: 16 },
        { header: "Talent Scan Completed", key: "tsDone", width: 16 },
        { header: "Talent Scan Score", key: "tsScore", width: 14 },
        { header: "Talent Scan Attempts", key: "tsAttempts", width: 14 },
        { header: "Timezone", key: "timezone", width: 18 },
      ]
      ws.columns = columns

      // ── Header row styling ──
      const headerRow = ws.getRow(1)
      headerRow.eachCell(cell => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF000053" },
        }
        cell.font = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 }
        cell.alignment = { vertical: "middle", horizontal: "left" }
        cell.border = {
          top: { style: "thin", color: { argb: "FFE4E7EC" } },
          bottom: { style: "thin", color: { argb: "FFE4E7EC" } },
          left: { style: "thin", color: { argb: "FFE4E7EC" } },
          right: { style: "thin", color: { argb: "FFE4E7EC" } },
        }
      })
      headerRow.height = 22

      // ── Data rows ──
      visibleRows.forEach((c, idx) => {
        const scStatus = statusOf(c._screen)
        const tsStatus = statusOf(c._scan)
        const row = ws.addRow({
          name: c.name,
          email: c.email,
          phone: c.phone,
          stage: c.stage,
          scStatus,
          scSent: c._screen?.sent_at || c._screen?.sent || "—",
          scSched:
            c._screen?.scheduled_at || c._screen?.scheduled || "—",
          scDone:
            c._screen?.completed_at || c._screen?.completed || "—",
          scScore: c._screen?.score != null ? c._screen.score : "—",
          scAttempts: c._screenHistory?.length || 0,
          tsStatus,
          tsSent: c._scan?.sent_at || c._scan?.sent || "—",
          tsSched: c._scan?.scheduled_at || c._scan?.scheduled || "—",
          tsDone: c._scan?.completed_at || c._scan?.completed || "—",
          timezone: reportTimezone || "Timezone unavailable",
          tsScore: c._scan?.score != null ? round2(c._scan.score) : "—",
          tsAttempts: c._scanHistory?.length || 0,
        })

        // zebra base fill for the whole row
        const zebra = idx % 2 === 1 ? "FFFBFBFD" : "FFFFFFFF"
        row.eachCell(cell => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: zebra } }
          cell.border = {
            top: { style: "thin", color: { argb: "FFEEF0F4" } },
            bottom: { style: "thin", color: { argb: "FFEEF0F4" } },
            left: { style: "thin", color: { argb: "FFEEF0F4" } },
            right: { style: "thin", color: { argb: "FFEEF0F4" } },
          }
          cell.font = { size: 10.5, color: { argb: "FF1F2430" } }
          cell.alignment = { vertical: "middle" }
        })

        // status pill colors
        const scCell = row.getCell("scStatus")
        const scColor = STATUS_COLORS[scStatus] || STATUS_COLORS["None"]
        scCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: scColor.fill } }
        scCell.font = { bold: true, size: 10.5, color: { argb: scColor.font } }
        scCell.alignment = { vertical: "middle", horizontal: "center" }

        const tsCell = row.getCell("tsStatus")
        const tsColor = STATUS_COLORS[tsStatus] || STATUS_COLORS["None"]
        tsCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: tsColor.fill } }
        tsCell.font = { bold: true, size: 10.5, color: { argb: tsColor.font } }
        tsCell.alignment = { vertical: "middle", horizontal: "center" }

        // stage color
        const stageCell = row.getCell("stage")
        stageCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: STAGE_COLORS[c.stage] || "FFF3F4F6" },
        }
        stageCell.alignment = { vertical: "middle", horizontal: "center" }

        // score cells: bold + center
        ;["scScore", "tsScore"].forEach(k => {
          const cell = row.getCell(k)
          cell.alignment = { vertical: "middle", horizontal: "center" }
          cell.font = { ...cell.font, bold: true }
        })
      })

      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: columns.length },
      }

      const buf = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], {
        type: "application/octet-stream",
      })
      const title =
        selectedJob?.job_title?.replace(/[\\/:*?"<>|]/g, "_") ||
        "Screening_Report"
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${title}_Screening_Report.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error("Export failed", e)
    }
  }

  const columns = useMemo(
    () => [
      {
        name: "Candidate Name",
        id: "name",
        selector: row => row.name,
        sortable: true,
        grow: 1.4,
        cell: row => (
          <div className="person">
            <div className="avatar">{initials(row.name)}</div>
            <div>
              <div className="p-name">{row.name}</div>
              <div className="p-sub">{row.email}</div>
              {row.phone && row.phone !== "—" && (
                <div className="p-sub">{row.phone}</div>
              )}
              {row.recruiter && row.recruiter !== "—" && (
                <div className="jobtag">by {row.recruiter}</div>
              )}
            </div>
          </div>
        ),
      },
      {
        name: "AI Screening Call",
        id: "_screenTime",
        selector: row => row._screenTime,
        sortable: true,
        grow: 1.6,
        cell: row => (
          <TimelineCell
            test={row._screen}
            badgeMap={SCREEN_BADGE}
            history={row._screenHistory}
            onViewHistory={() => openHistory(row, "screen")}
            showResult={false}
          />
        ),
      },
      {
        name: "Talent Scan Test",
        id: "_scanTime",
        selector: row => row._scanTime,
        sortable: true,
        grow: 1.6,
        cell: row => (
          <ScanCell
            candidate={row}
            onViewHistory={() => openHistory(row, "scan")}
          />
        ),
      },
      {
        name: "Stage",
        id: "stage",
        selector: row => row.stage,
        sortable: true,
        grow: 0.8,
        cell: row => (
          <span className={`badge ${STAGE_BADGE[row.stage] || "gray"}`}>
            {row.stage}
          </span>
        ),
      },
    ],
    []
  )

  const handleSort = (column, direction) => {
    // Server-side data is small per page; sort client-side on visibleRows by mutating order via custom sort
    // react-data-table-component calls this on header click; we just let it sort internally via sortFunction below
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const ALL_VALUE = "all"
  const paginationComponentOptions = {
    selectAllRowsItem: true,
    selectAllRowsItemText: "All",
  }

  return (
    <div className="jor-page">
      <style>{`
        .cellwrap {}
        .attempt-count {
          font-size: 11px; font-weight: 600; color: var(--muted, #6b7280);
          background: #eef1f6; border-radius: 999px; padding: 1px 7px;
        }
        .hist-table td.dt { white-space: nowrap; }
        .hist-link {
          margin-top: 6px; border: none; background: none; padding: 0;
          font-size: 11.5px; font-weight: 600; color: var(--orange, #ee751d); cursor: pointer;
        }
        .hist-link:hover { text-decoration: underline; }
        .hist-overlay {
          position: fixed; inset: 0; background: rgba(15, 18, 30, 0.45);
          display: flex; align-items: center; justify-content: center; z-index: 99999; isolation: isolate;
        }
        .hist-modal {
          background: #fff; border-radius: 14px; 
          width: min(860px, 95vw);
           max-height: 82vh;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: 0 24px 60px rgba(15, 18, 30, 0.28);
          border: 1px solid var(--line, #e4e7ec);
        }
        .hist-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 18px 22px; border-bottom: 1px solid var(--line, #e4e7ec); background: #fafbfc; }
        .hist-title { font-size: 15px; font-weight: 700; color: var(--navy, #000053); }
        .hist-sub { font-size: 14px; color: var(--muted, #6b7280); margin-top: 2px; }
        .hist-close { border: none; background: #fff; width: 28px; height: 28px; border-radius: 8px; cursor: pointer; font-size: 13px; color: var(--muted, #6b7280); border: 1px solid var(--line, #e4e7ec); }
        .hist-close:hover { background: #f4f6fa; }
        .hist-body { padding: 16px 22px 22px; overflow-y: auto; }

        .hist-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 12.5px;
          border: 1px solid var(--line, #e4e7ec);
          border-radius: 10px;
          overflow: hidden;
        }
        .hist-table thead tr { background: #f4f6fa; }
        .hist-table th {
          text-align: left;
          font-size: 11px;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: var(--muted, #6b7280);
          font-weight: 700;
          padding: 10px 12px;
          border-bottom: 2px solid var(--line, #e4e7ec);
          border-right: 1px solid var(--line, #e4e7ec);
        }
        .hist-table th:last-child { border-right: none; }
        .hist-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #eef0f4;
          border-right: 1px solid #f1f3f7;
          color: #1f2430;
        }
        .hist-table td:last-child { border-right: none; }
        .hist-table tr:last-child td { border-bottom: none; }
        .hist-table tr.latest td { background: #fff8f1; }
        .hist-table tr:hover td { background: #fafbfd; }
        .hist-table tr.latest:hover td { background: #fff4e6; }
        .hist-table .muted { color: var(--muted, #6b7280); }
        .hist-table a { color: var(--orange, #ee751d); font-weight: 600; }
        .report-job-select { width: 100%; display: flex; justify-content: space-between; align-items: center; border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; padding: 8px 12px; color: #334155; text-align: left; }
        .report-job-menu { position: absolute; top: calc(100% + 5px); left: 0; width: 100%; background: #fff; border: 1px solid #d7dee8; border-radius: 6px; box-shadow: 0 8px 20px rgba(15, 23, 42, .14); z-index: 100; overflow: hidden; }
        .report-job-menu > input { width: calc(100% - 18px); margin: 9px; padding: 7px 9px; border: 1px solid #d7dee8; border-radius: 4px; }
        .report-job-options { max-height: 250px; overflow-y: auto; padding: 0 6px; }
        .report-job-options button { display: block; width: 100%; border: 0; background: #fff; padding: 10px 10px; text-align: left; color: #475569; }
        .report-job-options button:hover, .report-job-options button.active { background: #e1f2ff; color: #0f172a; }
        .report-job-empty { padding: 14px 10px; color: #94a3b8; }
        .report-job-pagination { display: flex; align-items: center; justify-content: space-between; gap: 6px; padding: 8px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #64748b; }
        .report-job-pagination button { border: 1px solid #cbd5e1; border-radius: 4px; background: #fff; padding: 4px 7px; color: #64748b; }
        .report-job-pagination button:disabled { opacity: .45; cursor: not-allowed; }
      `}</style>
      <div className="content">
        <div className="page-head">
          <div>
            <h1>AI Screening &amp; Talent Scan Report</h1>
            <div className="sub">
              Candidate-level AI screening and Talent Scan activity per job
              order
            </div>
            <div className="sub" style={{ marginTop: 4 }}>
              Timezone: <b>{timezoneLabel}</b>
            </div>
          </div>
        </div>

        <div className="selbar">
          <span className="lbl">Job Order</span>
          {jobsLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Spinner size="sm" />
              <span style={{ fontSize: 12, color: "var(--muted)" }}>
                Loading jobs…
              </span>
            </div>
          ) : (
            <div style={{ position: "relative", width: 300 }}>
              <button
                type="button"
                className="report-job-select"
                onClick={() => setJobDropdownOpen(open => !open)}
              >
                {(() => {
                  const selected = jobList.find(j => String(j.id || j.job_id) === String(selectedJobId))
                  return selected ? (selected.job_title || selected.position || `Job #${selectedJobId}`) : "All Jobs"
                })()}
                <span>{jobDropdownOpen ? "⌃" : "⌄"}</span>
              </button>
              {jobDropdownOpen && (
                <div className="report-job-menu">
                  <input
                    autoFocus
                    value={jobSearch}
                    placeholder="Search job & press Enter..."
                    onChange={event => setJobSearch(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === "Enter") fetchJobs(1, jobSearch)
                    }}
                  />
                  <div className="report-job-options">
                    {jobList.map(j => {
                      const id = String(j.id || j.job_id || "")
                      const title = j.job_title || j.position || `Job #${id}`
                      return (
                        <button
                          type="button"
                          key={id}
                          className={id === String(selectedJobId) ? "active" : ""}
                          onClick={() => {
                            setSelectedJobId(id)
                            setJobDropdownOpen(false)
                          }}
                        >
                          {title}
                        </button>
                      )
                    })}
                    {!jobList.length && <div className="report-job-empty">No jobs found.</div>}
                  </div>
                  <div className="report-job-pagination">
                    <button type="button" disabled={jobsLoading || jobPage <= 1} onClick={() => fetchJobs(jobPage - 1, jobSearch)}>‹ Prev</button>
                    <span>Page {jobPage} / {Math.max(1, Math.ceil(jobTotal / JOB_PAGE_SIZE))}</span>
                    <button type="button" disabled={jobsLoading || jobPage >= Math.ceil(jobTotal / JOB_PAGE_SIZE)} onClick={() => fetchJobs(jobPage + 1, jobSearch)}>Next ›</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {jobsError && (
            <span style={{ color: "var(--red)", fontSize: 12 }}>
              {jobsError}
            </span>
          )}
          <span className="meta">
            {selectedJob && !reportLoading && (
              <>
                <b>{selectedJob.job_title}</b>
                {selectedJob.category_name
                  ? ` · ${selectedJob.category_name}`
                  : ""}
                {" · "}
                <b>{totalCount}</b> Candidates
               {screeningPct != null && (
                <span className="cutoff-bubble">
                  Screening Cut Off : <b>{screeningPct}</b>
                </span>
              )}
            </>
            )}
          </span>
        </div>

        {!reportLoading && allCandidates.length > 0 && (
          <div className="stat-groups">
            <div className="stat-card">
              <div className="sc-head">
                <span className="ico blue">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  >
                    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.45c.9.34 1.84.57 2.8.7a2 2 0 0 1 1.7 2.05Z" />
                  </svg>
                </span>
                <h3>AI Screening Calls</h3>
              </div>
              <div className="sc-body">
                <div className="sc-item">
                  <div className="v gray">{stats.scSent}</div>
                  <div className="l">Sent</div>
                </div>
                <div className="sc-item">
                  <div className="v amber">{stats.scSched}</div>
                  <div className="l">Scheduled</div>
                </div>
                <div className="sc-item">
                  <div className="v green">{stats.scDone}</div>
                  <div className="l">Completed</div>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="sc-head">
                <span className="ico orange">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  >
                    <path d="M9 11l3 3 8-8" />
                    <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
                  </svg>
                </span>
                <h3>Talent Scan Tests</h3>
              </div>
              <div className="sc-body">
                <div className="sc-item">
                  <div className="v gray">{stats.tsElig}</div>
                  <div className="l">Sent</div>
                </div>
                <div className="sc-item">
                  <div className="v gray">{stats.tsSent}</div>
                  <div className="l">Scheduled</div>
                </div>
                <div className="sc-item">
                  <div className="v green">{stats.tsDone}</div>
                  <div className="l">Completed</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="panel">
          {!reportLoading && selectedJobId && (
            <div className="toolbar">
              <span className="lbl">Screening</span>
              <select
                value={screenFilter}
                onChange={e => {
                  const val = e.target.value
                  setScreenFilter(val)
                  fetchReportPage(selectedJobId, 1, { screenFilter: val })
                }}
              >
                <option value="all">All</option>
                <option>Completed</option>
                <option>Scheduled</option>
                <option>Sent</option>
              </select>

              <span className="lbl">Talent Scan</span>
              <select
                value={scanFilter}
                onChange={e => {
                  const val = e.target.value
                  setScanFilter(val)
                  fetchReportPage(selectedJobId, 1, { scanFilter: val })
                }}
              >
                <option value="all">All</option>
                <option>Completed</option>
                <option>Scheduled</option>
                <option>Sent</option>
              </select>

             <span className="lbl">From</span>
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={e => {
                  const val = e.target.value
                  setFromDate(val)
                  if (val && toDate && new Date(val) > new Date(toDate)) {
                    setToDate("")
                    fetchReportPage(selectedJobId, 1, { fromDate: val, toDate: "" })
                  } else {
                    fetchReportPage(selectedJobId, 1, { fromDate: val })
                  }
                }}
              />
              <span className="lbl">To</span>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={e => {
                  const val = e.target.value
                  if (fromDate && val && new Date(val) < new Date(fromDate)) {
                    setToDate("")
                    fetchReportPage(selectedJobId, 1, { toDate: "" })
                  } else {
                    setToDate(val)
                    fetchReportPage(selectedJobId, 1, { toDate: val })
                  }
                }}
              />

              <button className="reset" onClick={handleReset}>
                Reset
              </button>
              <span className="count-bubble">
                  Count : <b>{totalCount}</b>
                </span>
            <div className="right search-box">
              <input
                type="search"
                value={search}
                placeholder="Search candidates.."
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
                <button onClick={handleExport} className="btn btn-md blueBtn">Excel</button>
              </div>
            )}

          {!selectedJobId ? (
            <div
              className="empty"
              style={{
                minHeight: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--line)"
                strokeWidth="1.5"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
              </svg>
              Select a job order to view the screening report
            </div>
          ) : reportError ? (
            <div
              style={{
                margin: 16,
                background: "var(--red-soft)",
                border: "1px solid var(--red)",
                borderRadius: 8,
                padding: "14px 18px",
                color: "var(--red)",
                fontSize: 12.5,
              }}
            >
              {reportError}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={visibleRows}
              progressPending={reportLoading}
              progressComponent={
                <div
                  className="empty"
                  style={{
                    minHeight: 200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  <Spinner color="primary" />
                  <span>Loading screening report…</span>
                </div>
              }
              noDataComponent={
                <div
                  className="empty"
                  style={{
                    minHeight: 200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--line)"
                    strokeWidth="1.5"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.5-3.5" />
                  </svg>
                  <span>No screening data found for this job order.</span>
                  <span
                    style={{ fontSize: 12, color: "var(--muted, #6b7280)" }}
                  >
                    Try widening the date range or clearing filters.
                  </span>
                  <button
                    type="button"
                    onClick={handleReset}
                    style={{
                      marginTop: 4,
                      border: "1px solid var(--line, #e4e7ec)",
                      background: "#fff",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "var(--orange, #ee751d)",
                      cursor: "pointer",
                    }}
                  >
                    Reset filters
                  </button>
                </div>
              }
              customStyles={dtStyles}
              highlightOnHover
              pagination
              paginationServer
              paginationTotalRows={totalCount}
              paginationDefaultPage={serverPage}
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[10, 20, 30]}
              onChangePage={pg => fetchReportPage(selectedJobId, pg)}
              onChangeRowsPerPage={handlePerRowsChange}
              fixedHeader
              fixedHeaderScrollHeight="520px"
              sortServer={false}
              defaultSortFieldId="__none__"
              paginationComponentOptions={paginationComponentOptions}
            />
          )}
        </div>
      </div>

      <HistoryPanel view={historyView} onClose={() => setHistoryView(null)} />
    </div>
  )
}
